import { useNavigate } from 'react-router';
import FileSaver from 'file-saver';
import {
    Box,
    Flex,
    Heading,
    Text,
    Button,
    Icon,
    useToast,
    Modal,
    ModalContent,
    ModalOverlay,
    ModalHeader,
    ModalFooter,
    ModalCloseButton,
    ModalBody,
    useDisclosure,
    chakra,
} from '@chakra-ui/react';
import PageHeader from 'layout/pageHeader';
import MDropZone from 'components/molecules/MDropZone';
import { useRef, useState } from 'react';
import { EXCEL_FILE_TYPE } from 'shared/const';
import icons from 'shared/icons';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { linkStyle } from 'shared/styles';
import { useTranslation } from 'helpers/languages/translations';
import {
    useExportKeywordCategoriesErrorsMutation,
    useLazyExportKeywordCategoriesQuery,
    useUploadKeywordCategoriesMutation,
} from 'core/API/keywordCategoriesApi';
import { IKeywordCategoriesExcelData, IKeywordCategoriesUploadError } from '../types';

const KeywordCategoriesUpload = () => {
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploadKeywordCategories, { isLoading }] = useUploadKeywordCategoriesMutation({
        fixedCacheKey: 'keyword-categories-upload-key',
    });
    const [exportKeywordCategories, { isLoading: isExportLoading, isFetching }] = useLazyExportKeywordCategoriesQuery();
    const [exportKeywordCategoriesErrors] = useExportKeywordCategoriesErrorsMutation();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [modalType, setModalType] = useState<'overwrite' | 'error'>('overwrite');
    const [errorList, setErrorList] = useState<IKeywordCategoriesExcelData[]>([]);
    const [faultyRowCount, setFaultyRowCount] = useState<number>(0);

    const navigate = useNavigate();
    const translate = useTranslation();
    const goBack = () => {
        navigate(-1);
    };
    const toast = useToast();
    const overwriteInitialRef = useRef(null);
    const [uploadMethod, setUploadMethod] = useState<'keep' | 'overwrite'>('overwrite');
    const handleChangeFile = (file: File | null) => {
        if (file?.type === EXCEL_FILE_TYPE) {
            setUploadedFile(file);
        } else {
            if (file !== null)
                toast({
                    title: translate('error.upload.file-invalid-type.xlsx'),
                    status: 'error',
                    isClosable: true,
                });
            setUploadedFile(null);
        }
    };

    const handleExportKeywordCategories = async () => {
        try {
            if (!isExportLoading && !isFetching) {
                const { data: res, isError: isExportError } = await exportKeywordCategories({
                    isTemplate: false,
                });
                if (isExportError || !res) throw new Error(translate('keyword.export.error') as string);

                const blob = new Blob([res], {
                    type: EXCEL_FILE_TYPE,
                });

                FileSaver.saveAs(blob, translate('categories_keywords.export.file_name') as string);
            }
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const renderDescriptionSection = () => {
        const UploadIcon = icons.upload;

        const handleUploadKeywords = async () => {
            try {
                const bodyFormData = new FormData();
                bodyFormData.append('excelFile', uploadedFile as File);
                bodyFormData.append('uploadMethod', uploadMethod);
                const res = (await uploadKeywordCategories(bodyFormData)) as IKeywordCategoriesUploadError;
                if (res.error) {
                    const errorType = res.error?.data?.type;
                    if (errorType === 'missing_headers') {
                        const missingHeaders = res.error?.data?.missing_headers;

                        if (missingHeaders?.length)
                            throw new Error(
                                translate('categories_keywords.error.missing_specific_headers', {
                                    headers: missingHeaders.join(', '),
                                }) as string,
                            );
                        else throw new Error(translate('categories_keywords.error.missing_headers') as string);
                    }
                    if (errorType === 'invalid_data') {
                        const data = res.error?.data?.data;
                        if (data) setErrorList(data);
                        setFaultyRowCount(res.error?.data?.faulty_row_count || 0);
                        setModalType('error');
                        onOpen();
                        return;
                    }
                    throw new Error(translate('error.generic') as string);
                } else {
                    goBack();
                }
            } catch (error) {
                toast({
                    title: (error as Error).message,
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        const handleDownloadErrorFile = async () => {
            try {
                const res = await exportKeywordCategoriesErrors(errorList);
                if ('error' in res) throw new Error(translate('error.export') as string);

                const blob = new Blob([res.data], {
                    type: EXCEL_FILE_TYPE,
                });

                FileSaver.saveAs(blob, translate('categories_keywords.export.error_file') as string);
            } catch (error) {
                toast({
                    title: (error as Error).message || translate('error.generic'),
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        const renderModal = () => {
            const overwirteTitle =
                uploadMethod === 'overwrite'
                    ? translate('keyword.upload.modal.overwriteHeader')
                    : translate('categories_keywords.upload.modal.error.title');
            const title =
                modalType === 'overwrite' ? overwirteTitle : translate('categories_keywords.upload.modal.error.title');
            const owModalContent =
                uploadMethod === 'overwrite' ? (
                    <>
                        <Text>{translate('categories_keywords.upload.modal.overwriteDesc')}</Text>
                        <Text>{translate('keyword.upload.modal.overwriteDesc2')}</Text>
                    </>
                ) : (
                    <Text>{translate('categories_keywords.upload.modal.overwriteDesc')}</Text>
                );

            const submitButton =
                modalType === 'overwrite' ? (
                    <Button
                        onClick={intentionalyFloatingPromiseReturn(handleUploadKeywords)}
                        isLoading={isLoading}
                        colorScheme="red"
                        size="sm"
                    >
                        {uploadMethod === 'overwrite'
                            ? translate('keyword.upload.modal.overwrite')
                            : translate('keyword.upload.modal.apply')}
                    </Button>
                ) : (
                    <Button isDisabled={isLoading} variant="outline" size="sm" mr={3} onClick={onClose}>
                        {translate('keyword.upload.modal.cancel')}
                    </Button>
                );

            const bodyContent =
                modalType === 'overwrite' ? (
                    owModalContent
                ) : (
                    <Text>
                        {translate('page.keyword.errorsUploadedDescription', {
                            nb: faultyRowCount,
                            downloadComp: (
                                <chakra.span
                                    onClick={intentionalyFloatingPromiseReturn(handleDownloadErrorFile)}
                                    {...linkStyle}
                                >
                                    {translate('page.keyword.errorDownload')}
                                </chakra.span>
                            ),
                        })}
                    </Text>
                );

            return (
                <Modal isCentered initialFocusRef={overwriteInitialRef} isOpen={isOpen} onClose={onClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader tabIndex={-1} ref={overwriteInitialRef}>
                            <Text as="h3" color={modalType === 'error' ? 'red.600' : 'gray.900'}>
                                {title}
                            </Text>
                        </ModalHeader>
                        <ModalCloseButton mt="2" isDisabled={isLoading} />
                        <ModalBody fontSize="sm">{bodyContent}</ModalBody>

                        <ModalFooter>
                            {modalType === 'overwrite' && (
                                <Button isDisabled={isLoading} variant="outline" size="sm" mr={3} onClick={onClose}>
                                    {translate('keyword.upload.modal.cancel')}
                                </Button>
                            )}
                            {submitButton}
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            );
        };

        const handleUploadChecker = async () => {
            if (uploadMethod === 'keep') {
                await handleUploadKeywords();
            } else {
                setModalType('overwrite');
                onOpen();
            }
        };

        return (
            <Flex w="40%" justifyContent="flex-start" direction="column" textAlign="start">
                <Text fontSize="md">
                    {translate('categories_keywords.upload.recommendation', {
                        template: (
                            <Text
                                onClick={intentionalyFloatingPromiseReturn(handleExportKeywordCategories)}
                                as="span"
                                {...linkStyle}
                            >
                                {translate('categories_keywords.upload.downloadTemplate')}
                            </Text>
                        ),
                    })}
                </Text>
                <Text fontSize="md" pt={4} mb={2}>
                    {translate('keyword.upload.file')}
                </Text>
                <Box py={4}>
                    <MDropZone chosenFile={uploadedFile} handleChangeFile={handleChangeFile} />
                </Box>
                <Flex mt="24px" justifyContent="space-between">
                    <Button
                        isDisabled={!uploadedFile}
                        rightIcon={<Icon as={UploadIcon} />}
                        isLoading={isLoading}
                        onClick={intentionalyFloatingPromiseReturn(handleUploadChecker)}
                        colorScheme="blue"
                        bg="blueMain"
                    >
                        {translate('keyword.upload.apply')}
                    </Button>
                </Flex>
                {renderModal()}
            </Flex>
        );
    };

    return (
        <Box>
            <PageHeader
                pageTitle={translate('categories_keywords.upload.title') as string}
                icon={{ name: 'arrowBack', props: { onClick: goBack } }}
            />
            <Box px="32px">
                <Flex justifyContent="start">
                    <Heading as="h2" fontWeight="semibold" fontSize="18px" mb="18px">
                        {translate('keyword.upload.header')}
                    </Heading>
                </Flex>
                <Flex fontSize="sm" direction="row">
                    {renderDescriptionSection()}
                </Flex>
            </Box>
        </Box>
    );
};

export default KeywordCategoriesUpload;
